import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import { cmsProductPages, type CmsProductPageIndexEntry } from '@/content/cms/product-pages.generated'
import {
  fetchLiveProductPage,
  localeOf,
  productPageCopy,
} from '@/content/cms/productPageLocale'

/**
 * HarbourTeaser — homepage featured spotlight for My Harbour.
 *
 * As of M3 this block reads its hero copy from the `cms_product_pages` snapshot
 * of the harbour row, falling back to `home.harbourTeaser.*` i18n if the
 * sync hasn't run. Centralising the chrome on cms_product_pages removes one of
 * the four duplicate-title strings the second-Explore agent surfaced —
 * `home.harbourTeaser.title`, `products.harbourTeaser.title`,
 * `harbour.hero.title`, `harbour.breadcrumb` — without forcing the admin
 * to also edit `home.harbourTeaser.*` separately.
 *
 * The copy is resolved for the READER's locale, like every other CMS-backed
 * block: the resolver falls back to the zh-Hant copy when a locale has no
 * copy of its own.
 *
 * Visual mirrors the My Harbour teaser card on /products (gradient + anchor
 * decoration + accent badge) but at homepage hero-level scale.
 */
export default function HarbourTeaser() {
  const { t, i18n } = useTranslation()
  const locale = localeOf(i18n.language)

  // Pull from generated snapshot first; if empty (cold start) fall back to
  // the live API, then to the legacy i18n keys.
  const initial = cmsProductPages.find((p) => p.product_slug === 'harbour') ?? null
  const [live, setLive] = useState<CmsProductPageIndexEntry | null>(initial)

  useEffect(() => {
    if (initial) return
    let cancelled = false
    void fetchLiveProductPage('harbour', locale).then((row) => {
      if (cancelled) return
      if (row) setLive(row)
    })
    return () => {
      cancelled = true
    }
  }, [initial, locale])

  const hero = (
    (live ? productPageCopy(live, locale).blocks : null) as
      | { hero?: { title?: string; subtitle?: string; description?: string } }
      | null
  )?.hero

  const badge = t('home.harbourTeaser.badge')
  const title = hero?.title || t('home.harbourTeaser.title')
  const subtitle = hero?.subtitle || t('home.harbourTeaser.subtitle')
  const description = hero?.description || t('home.harbourTeaser.description')
  const ctaHarbour = t('home.harbourTeaser.ctaHarbour')

  return (
    <section
      className="py-section-y px-section-x bg-surface-2"
      aria-labelledby="home-harbour-teaser-title"
    >
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-yada-xl bg-gradient-to-br from-yada-coffee via-yada-brown to-yada-olive text-surface shadow-yada-lg">
            {/* Background anchor decoration */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-12 -bottom-16 hidden md:block"
            >
              <Icon
                icon="lucide:anchor"
                className="text-accent/30"
                style={{ fontSize: '20rem' }}
              />
            </div>

            <div className="relative z-10 p-6 sm:p-8 md:p-12 lg:p-16 grid md:grid-cols-[1fr_auto] gap-6 md:gap-12 items-center">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  {/* The mobile anchor icon — kept right next to the badge so
                      phones get the same decorative anchor+sparkle that the
                      desktop layout gets from the large background anchor. */}
                  <div className="md:hidden w-12 h-12 rounded-yada-xl bg-surface/10 backdrop-blur flex items-center justify-center border border-surface/20 flex-shrink-0">
                    <Icon
                      icon="lucide:anchor"
                      className="text-accent"
                      style={{ fontSize: '1.5rem' }}
                    />
                  </div>
                  <span className="inline-flex items-center gap-1.5 bg-accent text-ink px-3 py-1 rounded-yada-full text-xs font-bold">
                    <Icon icon="lucide:sparkles" className="text-sm" />
                    {badge}
                  </span>
                </div>

                <h2
                  id="home-harbour-teaser-title"
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-surface leading-tight mb-3 text-balance"
                >
                  {title}
                </h2>

                <p className="text-lg sm:text-xl md:text-2xl text-accent font-semibold mb-4 md:mb-5 leading-snug">
                  {subtitle}
                </p>

                <p className="text-surface/90 leading-relaxed mb-6 md:mb-8 max-w-xl text-pretty">
                  {description}
                </p>

                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                  <Link
                    to="/products/harbour"
                    className="inline-flex items-center justify-center gap-2 bg-accent text-ink px-6 py-3 rounded-yada-full font-semibold hover:bg-accent-soft transition-colors focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 w-full sm:w-auto"
                  >
                    {ctaHarbour}
                    <Icon icon="lucide:arrow-right" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}
